import { Component, Input, ViewChild, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormControl, NgForm } from '@angular/forms';
import { MatPaginator, MatSort, MatTableDataSource } from '@angular/material';
import * as XLSX from 'xlsx';
import { AuthProvider } from '../../providers/auth/auth';
import _ from 'lodash';
import { BehaviorSubject, Subject } from 'rxjs';
import { AlertController, LoadingController } from 'ionic-angular';
import { UtilityProvider } from '../../providers/utility/utility';
import { PatientProvider } from '../../providers/patient/patient';
import { DatabaseProvider } from '../../providers/database/database';
import { PdfProvider } from '../../providers/pdf/pdf';
import { ENV } from '@app/env'

@Component({
  selector: 'patient-order',
  templateUrl: 'patient-order.html'
})
export class PatientOrderComponent implements OnInit {
  private ngUnsubscribe: Subject<any> = new Subject();
  program_id: string;
  program_name: string;
  patient: any;
  exportjson: any;
  private _program_id = new BehaviorSubject<any>([]);
  private _program_name = new BehaviorSubject<any>([]);
  private _patient = new BehaviorSubject<any>([]);

  @Input()
  set programId(value) { this._program_id.next(value); }
  get programId() { return this._program_id.getValue(); }

  @Input()
  set programName(value) { this._program_name.next(value); }
  get programName() { return this._program_name.getValue(); }

  @Input()
  set patientData(value) { this._patient.next(value); }
  get patientData() { return this._patient.getValue(); }

  userid: string;
  user_fullname: string;
  user: any;
  pharmacies: any[] = [];
  therapies: any[] = [];
  dosages: any[] = [];
  orderPanelStatus = false;

  displayedColumns = ['order_number', 'commercial_compassionate','dose', 'doctor', 'pharmacy', 'repeats', 'quantity','notes', 'createdBy', 'createdAt', 'actions'];
  dataSource: any;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  addeditOrderForm: FormGroup; 
  @ViewChild('formOrderDirective') private formOrderDirective: NgForm;

  constructor(
    private readonly auth: AuthProvider,
    private loadingCtrl: LoadingController,
    private dbPatient: PatientProvider,
    private db: DatabaseProvider,
    private fb: FormBuilder,
    public alertCtrl: AlertController,
    public utility: UtilityProvider,
    private pdf: PdfProvider
    ) {
      this.createForm();

      this.auth.getCurrentUser().then(user => {
        this.userid = user.id;
        this.user_fullname = user.first_name + ' ' + user.last_name;
        this.user = { id: user.id, name: user.first_name + ' ' + user.last_name };

        this.db
        .getPharmaciesByProgram(this.program_id)
        .snapshotChanges()
        .map(actions => {
          return actions.map(a => {
            const data = a.payload.doc.data() as any;
            data.id = a.payload.doc.id;
            return data;
          });
        })
        .takeUntil(this.ngUnsubscribe)
        .subscribe((pharmacies) => {
          this.pharmacies = pharmacies;
        });

        this.db
        .getTherapyByProgram(this.program_id)
        .snapshotChanges()
        .map(actions => {
          return actions.map(a => {
            const data = a.payload.doc.data() as any;
            data.id = a.payload.doc.id;
            return data;
          });
        })
        .takeUntil(this.ngUnsubscribe)
        .subscribe((therapies) => {
          this.therapies = therapies;
          if (this.patient.therapy) {
            this.dosages = _.filter(this.therapies, therapy => therapy.name === this.patient.therapy)[0]['dosing'];
          }
        });

        this.dbPatient
        .listOrders(this.program_id, this.patient.id)
        .snapshotChanges()
        .map(actions => {
          return actions.map(a => {
            const data = a.payload.doc.data() as any;
            data.id = a.payload.doc.id;
            return data;
          });
        })
        .takeUntil(this.ngUnsubscribe)
        .subscribe(orders => {
          // console.log('Dispensing: ', dispensing);
          this.exportjson = orders;
          this.dataSource = new MatTableDataSource(orders);
          this.dataSource.paginator = this.paginator;
          this.dataSource.sort = this.sort;
        });
      });
    }

  ngOnInit()  {
    this._program_id
    .takeUntil(this.ngUnsubscribe)
    .subscribe(x => {
      // console.log('Program ID on Summary: ', x)
        this.program_id = x
    });

    this._program_name
    .takeUntil(this.ngUnsubscribe)
    .subscribe(x => {
      // console.log('Program Name on Summary: ', x)
        this.program_name = x
    });

    this._patient
    .takeUntil(this.ngUnsubscribe)
    .subscribe(x => {
      // console.log('Patient on Summary: ', x)
        this.patient = x
    });
  }

  ngOnDestroy() {
    console.log('Leave patient orders and destroy subscriptions');
    this._patient.unsubscribe();
    this._program_id.unsubscribe();
    this._program_name.unsubscribe();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  applyFilter(filterValue: string) {
    filterValue = filterValue.trim(); // Remove whitespace
    filterValue = filterValue.toLowerCase(); // Datasource defaults to lowercase matches
    this.dataSource.filter = filterValue;
  }

  export() {
    const workBook = XLSX.utils.book_new(); // create a new blank book
    const workSheet = XLSX.utils.json_to_sheet(this.exportjson);

    XLSX.utils.book_append_sheet(workBook, workSheet, 'Orders'); // add the worksheet to the book
    XLSX.writeFile(workBook, 'orders.xlsx'); // initiate a file download in browser
  }

  createForm()  {
    let emailregex: RegExp = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    let phoneregex: RegExp = /^\+[1-9]\d{1,14}(\se?x?t?(\d*))?$/

    this.addeditOrderForm = this.fb.group({
      'order_number': [null, [Validators.required]],
      'doctor': [null, [Validators.required]],
      'type': [null, [Validators.required]],
      'commercial_compassionate': [null, [Validators.required]],
      'dose': [null, [Validators.required]],
      'repeats': [null, [Validators.required]],
      'quantity': [null, [Validators.required]],
      'pharmacy': [null, [Validators.required]],
      'fax_number': [null, [Validators.required, Validators.pattern(phoneregex)]],
      'notes': [null]
    });

    this.onFormChanges();
  }

  async save()  {
    const loading = this.loadingCtrl.create({});
    loading.present();
    let order = this.addeditOrderForm.value;
    order.therapy = this.patient.therapy;
    order.indication = this.patient.indication;
    if (order.fax_number) {
      order.fax_number = this.utility.formate164(order.fax_number);
    } else {
      order.fax_number = null;
    }
    if (order.type === 'Drug') {
      order.to_email = ENV.order_drug_email_to;
      order.cc_email = ENV.order_drug_email_cc;
      order.bcc_email = ENV.order_drug_email_bcc;
    } else {
      order.to_email = ENV.order_other_email_to;
      order.cc_email = ENV.order_other_email_cc;
      order.bcc_email = ENV.order_other_email_bcc;
    }
    const patient = {
      enrolment_url: this.patient.enrolment_url ? this.patient.enrolment_url : null,
      first_name: this.patient.first_name,
      last_name: this.patient.last_name,
      address: this.patient.address,
      address2: this.patient.address2,
      city: this.patient.city,
      province: this.patient.province,
      postal_code: this.patient.postal_code,
      id: this.patient.id,
      program_patient_id: this.patient.program_patient_id,
      phone_number: this.patient.phone_number,
      email: this.patient.email,
      gender: this.patient.gender,
      height: this.patient.height ? this.patient.height : null,
      weight: this.patient.weight ? this.patient.weight : null,
      case_manager: this.user_fullname
    }
    order = { ...order, patient: patient, doctor: this.patient.doctor };
    this.patient.case_manager = this.user_fullname;
    const filepath: any = await this.pdf.createOrder(this.program_id, this.program_name, this.patient, order, this.user);
    order.url = filepath;
    this.dbPatient.addOrder(this.program_id, this.patient.id, order, this.patient, this.user)
    .then(() => {
      this.cancel();
      loading.dismiss();
      this.utility.presentToast('The order was successfully submitted');
    })
    .catch(err => {
      this.utility.presentToast('There was an issue saving the order.  Please try again.  Error : ' + err);
      loading.dismiss();
    });
  }

  cancel()  {
    this.orderPanelStatus = false;
    setTimeout(() => {
      this.formOrderDirective.resetForm();
      this.addeditOrderForm.reset();
    }, 0);
  }

  getUniqueOrderNumber()  {
    this.addeditOrderForm.controls['order_number'].setValue('Retrieving...');
    let loading = this.loadingCtrl.create({
      content: "Creating new order number"
    });

    loading.present().then(_ => {
      this.db.getUniqueOrderNumber(this.program_id).then(data => {
        const orderNumber = 'ORD' + this.utility.pad(data, 8, '0');
        this.addeditOrderForm.controls['order_number'].setValue(orderNumber);
        loading.dismiss();
      })
      .catch((err) => {
        this.addeditOrderForm.controls['order_number'].setValue(null);
      })
    });
  }

  getFaxNumber()  {
    const pharmacy = this.addeditOrderForm.controls['pharmacy'].value;
    this.addeditOrderForm.controls['fax_number'].setValue(pharmacy.fax_number);
  }

  onFormChanges(): void {
    this.addeditOrderForm.controls['type'].valueChanges
    .takeUntil(this.ngUnsubscribe)
    .subscribe(() => {
      if(this.addeditOrderForm.controls['type'].value === 'Drug') {
        this.addeditOrderForm.get('dose').setValidators([Validators.required]);
        this.addeditOrderForm.get('dose').updateValueAndValidity();
        
        this.addeditOrderForm.get('commercial_compassionate').setValidators([Validators.required]);
        this.addeditOrderForm.get('commercial_compassionate').updateValueAndValidity();

        this.addeditOrderForm.get('repeats').setValidators([Validators.required]);
        this.addeditOrderForm.get('repeats').updateValueAndValidity();
        
        this.addeditOrderForm.get('quantity').setValidators([Validators.required]);
        this.addeditOrderForm.get('quantity').updateValueAndValidity();

        this.addeditOrderForm.get('pharmacy').setValidators([Validators.required]);
        this.addeditOrderForm.get('pharmacy').updateValueAndValidity();

        this.addeditOrderForm.get('fax_number').setValidators([Validators.required]);
        this.addeditOrderForm.get('fax_number').updateValueAndValidity();

        this.addeditOrderForm.updateValueAndValidity();
      } else {
        this.addeditOrderForm.get('dose').setValidators(null);
        this.addeditOrderForm.get('dose').setErrors(null);
        this.addeditOrderForm.get('dose').updateValueAndValidity();
        
        this.addeditOrderForm.get('repeats').setValidators(null);
        this.addeditOrderForm.get('repeats').setErrors(null);
        this.addeditOrderForm.get('repeats').updateValueAndValidity();

        this.addeditOrderForm.get('commercial_compassionate').setValidators(null);
        this.addeditOrderForm.get('commercial_compassionate').setErrors(null);
        this.addeditOrderForm.get('commercial_compassionate').updateValueAndValidity();
        
        this.addeditOrderForm.get('quantity').setValidators(null);
        this.addeditOrderForm.get('quantity').setErrors(null);
        this.addeditOrderForm.get('quantity').updateValueAndValidity();

        this.addeditOrderForm.get('pharmacy').setValidators(null);
        this.addeditOrderForm.get('pharmacy').setErrors(null);
        this.addeditOrderForm.get('pharmacy').updateValueAndValidity();

        this.addeditOrderForm.get('fax_number').setValidators(null);
        this.addeditOrderForm.get('fax_number').setErrors(null);
        this.addeditOrderForm.get('fax_number').updateValueAndValidity();

        this.addeditOrderForm.updateValueAndValidity();
      }
    });
  }

  view(url) {
    window.open(url, '_blank');
  }
}



// WEBPACK FOOTER //
// ./src/components/patient-order/patient-order.ts